import React, { useState, useEffect } from 'react';
import { User } from '../../../types/manageUsers';
import PersonAvatar from './PersonAvatar';
import { HiX } from 'react-icons/hi';
import { isValidEmail } from '../../../utils/csvUtils';

interface EmailInputRowProps {
  user: User;
  email: string;
  onEmailChange: (userId: string, email: string) => void;
  onRemoveUser: (userId: string) => void;
}
const EmailInputRow: React.FC<EmailInputRowProps> = ({
  user,
  email,
  onEmailChange,
  onRemoveUser,
}) => {
  const [localEmail, setLocalEmail] = useState(email);
  const [isValid, setIsValid] = useState(true);
  const [hasBeenTouched, setHasBeenTouched] = useState(false);

  useEffect(() => {
    setLocalEmail(email);
    if (email && email.trim() !== '') {
      setIsValid(isValidEmail(email));
      setHasBeenTouched(true);
    }
  }, [email]);

  const handleEmailChange = (newEmail: string) => {
    setLocalEmail(newEmail);
    setHasBeenTouched(true);
    if (newEmail.length > 3 || (hasBeenTouched && newEmail.length > 0)) {
      const valid = isValidEmail(newEmail);
      setIsValid(valid);
    } else {
      setIsValid(true); // Don't show errors while user is still typing initial characters
    }
    
  };

  const handleBlur = () => {
    // Validation and update parent on blur (when user finishes editing
    setHasBeenTouched(true);
    const valid = isValidEmail(localEmail);
    setIsValid(valid);
    // Update parent component when user finishes editing
    onEmailChange(user.id, localEmail);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent form submission on Enter key
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur(); // Trigger blur to save the email
    }
  };

  const hasOriginalEmail = user.email && user.email.trim() !== '';

  return (
    <div className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
      <div className="flex items-center space-x-3">
        {/* User Info */}
        <div className="flex items-center space-x-3 flex-1">
          <div className="w-10 h-10">
            <PersonAvatar
              className="!rounded-md"
              name={user.display_name}
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {user.display_name}
              </p>
              <button
                onClick={() => onRemoveUser(user.id)}
                className="ml-2 p-1 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition-colors"
                title="Remove user from import"
              >
                <HiX className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {hasOriginalEmail ? `Original: ${user.email}` : 'No original email'}
            </p>
          </div>
        </div>
      </div>

      {/* Email Input */}
      <div className="mt-2">
        <label htmlFor={`email-${user.id}`} className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Email Address *
        </label>
        <input
          id={`email-${user.id}`}
          type="email"
          value={localEmail}
          onChange={(e) => handleEmailChange(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="Enter email address"
          className={`w-full px-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 transition-colors ${
            !isValid && localEmail && hasBeenTouched
              ? 'border-red-300 focus:border-red-500 focus:ring-red-200 dark:border-red-600 dark:focus:border-red-500 dark:focus:ring-red-900/20'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200 dark:border-gray-600 dark:focus:border-blue-400 dark:focus:ring-blue-900/20'
          } bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400`}
        />
        {!isValid && localEmail && hasBeenTouched && (
          <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">
            Please enter a valid email address
          </p>
        )}
        {!localEmail.trim() && hasBeenTouched && (
          <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">
            Email is required for import
          </p>
        )}
        {!hasBeenTouched && !localEmail.trim() && (
          <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
            Email is required for import
          </p>
        )}
      </div>
    </div>
  );
};

export default EmailInputRow;
